import { http } from '@noor/api';
import {
  ofdStatusLogSchema,
  selfEmployedRowSchema,
  selfEmploymentCheckResultSchema,
  type OfdStatusLog,
  type ReportPage,
  type SelfEmployedRow,
  type SelfEmploymentCheckResult,
} from '../model/schema';
import type { SelfEmployedFilter, SelfEmployedListFilter } from '../lib/filters';
import { toReportPage } from '../lib/pages';

/**
 * Self-employment report API — the exact cpanel paths, relative to the shared
 * `http` base. The list is JSON; the export is the backend's dedicated
 * `…/export` file endpoint (cpanel fed the list endpoint's JSON to its file
 * downloader, which cannot produce a file).
 */

export type CourierId = string | number;

export interface SelfEmploymentCheckBody {
  courier_id: number;
  pass_data?: string | null | undefined;
  pinfl?: string | null | undefined;
}

export interface OfdLogsFilter {
  courier_id: CourierId;
  from: string;
  till: string;
  limit: number;
  offset: number;
}

function toQuery(filter: object): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      query[key] = String(value);
    }
  }
  return query;
}

export const selfEmploymentApi = {
  /** `GET admin/couriers/self-employment-status-excel` — one page of couriers. */
  async list(filter: SelfEmployedListFilter): Promise<ReportPage<SelfEmployedRow>> {
    const raw = await http.get<unknown>('admin/couriers/self-employment-status-excel', {
      query: toQuery(filter),
    });
    return toReportPage(
      raw,
      filter.limit,
      Math.floor(filter.skip / filter.limit) + 1,
      (item) => selfEmployedRowSchema.safeParse(item).data,
    );
  },

  /** `GET …/self-employment-status-excel/export` — the whole filtered report. */
  async exportReport(filter?: SelfEmployedFilter): Promise<Blob> {
    return await http.getBlob('admin/couriers/self-employment-status-excel/export', {
      query: filter ? toQuery(filter) : {},
    });
  },

  /** `POST admin/couriers/self-employment-check` — re-check one courier at the OFD. */
  async check(body: SelfEmploymentCheckBody): Promise<SelfEmploymentCheckResult> {
    const raw = await http.post<unknown>('admin/couriers/self-employment-check', body);
    const payload =
      typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw;
    return selfEmploymentCheckResultSchema.parse(payload ?? {});
  },

  /**
   * `GET admin/couriers/:id/ofd-logs` — the courier's OFD status history. The
   * page cursor goes out as both `skip` (the documented contract) and
   * `offset` (what cpanel sent).
   */
  async ofdLogs(filter: OfdLogsFilter): Promise<ReportPage<OfdStatusLog>> {
    const raw = await http.get<unknown>(`admin/couriers/${filter.courier_id}/ofd-logs`, {
      query: toQuery({
        from: filter.from,
        till: filter.till,
        limit: filter.limit,
        skip: filter.offset,
        offset: filter.offset,
      }),
    });
    return toReportPage(
      raw,
      filter.limit,
      Math.floor(filter.offset / filter.limit) + 1,
      (item) => ofdStatusLogSchema.safeParse(item).data,
    );
  },
};
